import { FC, useCallback, useEffect, useRef, useState } from 'react';
import ColorThief from 'colorthief';

import cn from 'clsx';

type Props = {
  files?: FileList;
  imageSrc?: string;
  onChange?: (files: FileList) => void;
  disabled?: boolean;
  length?: number;
  multiple?: boolean;
  accept?: string;
  placeholder?: string;
  className?: string;
  neon?: boolean;
};

const BlockImagePicker: FC<Props> = ({
  files: propFiles,
  imageSrc,
  onChange,
  disabled = false,
  length = 1,
  accept = 'image/*',
  neon = false,
  placeholder = 'Upload image',
  className,
}) => {
  const [files, setFiles] = useState<FileList>();
  const [image, setImage] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [bgColor, setBgColor] = useState<string>('#221f20');
  const [imgLoading, setImgLoading] = useState<boolean>(false);

  const displayFiles = propFiles === undefined ? files : propFiles;

  useEffect(() => {
    if ((displayFiles?.length || 0) > 0) {
      const file = displayFiles?.[0];
      if (!file) return;

      if (typeof file === 'string') return setImage(file);
      try {
        if (typeof file.name !== 'string') {
          return;
        }

        const reader = new FileReader();

        reader.onload = function (e) {
          setImage(e.target?.result?.toString());
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.log('err reading image file', err);
      }
    }
  }, [displayFiles]);

  const _onChange = (e) => {
    setFiles(e.target.files);
    onChange?.(e.target.files);
  };

  const onClick = useCallback(() => {
    inputRef?.current?.dispatchEvent?.(new MouseEvent('click'));
  }, [inputRef]);

  return (
    <div
      className={cn(
        'transition duration-300 ease-in-out cursor-pointer w-full h-full flex flex-col justify-center align-center opacity-100',
        { 'opacity-0': imgLoading },
        className
      )}
      style={{ backgroundColor: bgColor }}
      role="button"
      onClick={disabled ? undefined : onClick}
    >
      <div className="invisible w-0 h-0">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={length > 1}
          onChange={_onChange}
        />
      </div>
      {image || imageSrc ? (
        <img
          ref={imgRef}
          className="object-contain w-full h-full"
          src={image || imageSrc}
          crossOrigin="anonymous"
          alt="selected-file"
          onLoadStart={() => setImgLoading(true)}
          onLoad={() => {
            setImgLoading(false);
            if (imgRef?.current) {
              const colorthief = new ColorThief();
              const palette = colorthief.getPalette(imgRef?.current);
              const color = palette?.[0];
              if (color) {
                setBgColor(`rgb(${color[0]},${color[1]},${color[2]})`);
              }
            }
          }}
        />
      ) : (
        <div className="w-1/2 mx-auto text-gray-500">
          <div
            className={`p-4 font-bold text-center border-${
              neon ? 0 : 1
            } border-gray-500 border-dashed font-display`}
          >
            {placeholder}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockImagePicker;
